import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';
import { Avatar } from 'primereact/avatar';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';

const PersonalSettings = () => {
  const toast = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [formData, setFormData] = useState({
    userNumber: 'SE001234',
    firstName: 'John',
    surname: 'Smith',
    email: 'john.smith@example.com',
    homePhone: '01234 567890',
    workPhone: '01234 567891',
    mobilePhone: '07123 456789',
    addressLine1: '123 Main Street',
    addressLine2: 'Apartment 4B',
    addressLine3: '',
    cityTown: 'London',
    county: 'Greater London',
    postcode: 'SW1A 1AA',
    gender: 'male',
    dateOfBirth: new Date('1985-06-15'),
    username: 'johnsmith85'
  });

  const genderOptions = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Other', value: 'other' },
    { label: 'Prefer not to say', value: 'not_specified' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Simulate save operation
    setEditMode(false);
    toast.current.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Personal settings updated successfully',
      life: 3000
    });
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset form data if needed
  };

  const onUpload = (event) => {
    const file = event.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicture(e.target.result);
      };
      reader.readAsDataURL(file);
      
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Profile picture uploaded successfully',
        life: 3000
      });
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      
      <div className="se-card">
        <div className="se-card-header">
          <div className="flex justify-content-between align-items-center">
            <h2>Personal Settings</h2>
            <div>
              {!editMode ? (
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  className="p-button-primary"
                  onClick={() => setEditMode(true)}
                />
              ) : (
                <div className="flex gap-2">
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-secondary"
                    onClick={handleCancel}
                  />
                  <Button
                    label="Save"
                    icon="pi pi-check"
                    className="p-button-primary"
                    onClick={handleSave}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="se-card-content">
          {/* Profile Picture Section */}
          <div className="text-center mb-4">
            <div className="inline-block">
              <Avatar
                image={profilePicture}
                icon={!profilePicture ? "pi pi-user" : null}
                size="xlarge"
                shape="circle"
                className="mb-3"
                style={{ width: '120px', height: '120px' }}
              />
              {editMode && (
                <div className="mt-2">
                  <FileUpload
                    mode="basic"
                    name="profilePicture"
                    accept="image/*"
                    maxFileSize={5000000}
                    onUpload={onUpload}
                    chooseLabel="Change Picture"
                    className="p-button-secondary"
                    auto
                  />
                </div>
              )}
            </div>
          </div>

          {/* User Number (Read-only) */}
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label className="se-form-label">User Number</label>
                <InputText
                  value={formData.userNumber}
                  className="w-full"
                  disabled
                />
                <small className="text-600">Your unique member identification number</small>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <h3 className="se-text-primary mb-3 mt-4">Personal Information</h3>
          
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="firstName" className="se-form-label">First Name</label>
                <InputText
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="surname" className="se-form-label">Surname</label>
                <InputText
                  id="surname"
                  value={formData.surname}
                  onChange={(e) => handleInputChange('surname', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
          </div>

          <div className="se-form-group">
            <label htmlFor="email" className="se-form-label">Email</label>
            <InputText
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full"
              disabled={!editMode}
            />
          </div>

          {/* Contact Information */}
          <h3 className="se-text-primary mb-3 mt-4">Contact Information</h3>
          
          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="homePhone" className="se-form-label">Home Phone</label>
                <InputText
                  id="homePhone"
                  value={formData.homePhone}
                  onChange={(e) => handleInputChange('homePhone', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="workPhone" className="se-form-label">Work Phone</label>
                <InputText
                  id="workPhone"
                  value={formData.workPhone}
                  onChange={(e) => handleInputChange('workPhone', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="mobilePhone" className="se-form-label">Mobile Phone</label>
                <InputText
                  id="mobilePhone"
                  value={formData.mobilePhone}
                  onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <h3 className="se-text-primary mb-3 mt-4">Address</h3>
          
          <div className="se-form-group">
            <label htmlFor="addressLine1" className="se-form-label">Address Line 1</label>
            <InputText
              id="addressLine1"
              value={formData.addressLine1}
              onChange={(e) => handleInputChange('addressLine1', e.target.value)}
              className="w-full"
              disabled={!editMode}
            />
          </div>

          <div className="se-form-group">
            <label htmlFor="addressLine2" className="se-form-label">Address Line 2</label>
            <InputText
              id="addressLine2"
              value={formData.addressLine2}
              onChange={(e) => handleInputChange('addressLine2', e.target.value)}
              className="w-full"
              disabled={!editMode}
            />
          </div>

          <div className="se-form-group">
            <label htmlFor="addressLine3" className="se-form-label">Address Line 3</label>
            <InputText
              id="addressLine3"
              value={formData.addressLine3}
              onChange={(e) => handleInputChange('addressLine3', e.target.value)}
              className="w-full"
              disabled={!editMode}
            />
          </div>

          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="cityTown" className="se-form-label">City/Town</label>
                <InputText
                  id="cityTown"
                  value={formData.cityTown}
                  onChange={(e) => handleInputChange('cityTown', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="county" className="se-form-label">County</label>
                <InputText
                  id="county"
                  value={formData.county}
                  onChange={(e) => handleInputChange('county', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="se-form-group">
                <label htmlFor="postcode" className="se-form-label">Postcode</label>
                <InputText
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
          </div>

          {/* Personal Details */}
          <h3 className="se-text-primary mb-3 mt-4">Personal Details</h3>
          
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="gender" className="se-form-label">Gender</label>
                <Dropdown
                  id="gender"
                  value={formData.gender}
                  options={genderOptions}
                  onChange={(e) => handleInputChange('gender', e.value)}
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="se-form-group">
                <label htmlFor="dateOfBirth" className="se-form-label">Date of Birth</label>
                <Calendar
                  id="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.value)}
                  dateFormat="dd/mm/yy"
                  className="w-full"
                  disabled={!editMode}
                />
              </div>
            </div>
          </div>

          {/* Account Details */}
          <h3 className="se-text-primary mb-3 mt-4">Account Details</h3>
          
          <div className="se-form-group">
            <label htmlFor="username" className="se-form-label">Username</label>
            <InputText
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              className="w-full"
              disabled={!editMode}
            />
          </div>

          {!editMode && (
            <div className="mt-4">
              <Message
                severity="info"
                text="Click 'Edit' to modify your personal information. Some fields may require verification for security purposes."
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalSettings;